import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ParagonInstallRequest } from "lib/generated/integration-api";
import { useIntegrationApi } from "lib/integrationApiContext";

/**
 * The provider types the integrations service records a Paragon-backed install under. Taken from
 * the generated request model so it stays in step with what the service actually accepts.
 */
export type ParagonProviderType = ParagonInstallRequest["providerType"];

/**
 * Tells the integrations service about a CRM connection made through Paragon, so it knows the
 * company has a CRM and which object to push to. Safe to call repeatedly — the service upserts.
 */
export function useRecordParagonInstall() {
  const api = useIntegrationApi();
  const queryClient = useQueryClient();

  return useMutation(
    (paragonInstallRequest: ParagonInstallRequest) =>
      api.recordParagonInstall({ paragonInstallRequest }),
    {
      // The connected CRM is cached for the session, so whoever reads it next — the configuration
      // page, straight after connecting — would otherwise be told no CRM is connected.
      //
      // The provider list too: recording a Paragon connection retires whatever native install the
      // company had, and the connect card reads that list to tell a migration that finished from
      // one whose recording failed. Stale, it would offer to migrate a CRM already migrated.
      // Returned, not fired and forgotten: react-query waits for what `onSuccess` returns, and the
      // caller navigates to a page that reads both of these the moment the mutation resolves.
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["integrationProvider"] }),
          queryClient.invalidateQueries({ queryKey: ["providerList"] }),
        ]),
    },
  );
}
